import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, EmptyState, Toggle } from '@playerbase/ui';
import { STARTER_FORM_NAME } from '@playerbase/types';

import { useShell } from '../../shell/use-shell.js';
import {
  createFeedbackForm,
  fetchFeedbackFormsForGame,
  type FeedbackFormWithCount,
} from '../../games/feedback-forms-data.js';
import { FormsList } from './forms-list.js';

// Banner dismissals live in localStorage keyed by the starter form's id
// (uuid, never reused). One starter per game; if the studio deletes it
// and reconnects, the seed RPC creates a new form with a new id and the
// banner shows again - which matches the studio's intent (they have a
// blank slate again).
const BANNER_STORAGE_PREFIX = 'feedback-forms.starter-banner.';

// ---------------------------------------------------------------------------
// Forms list section. Renders inside FeedbackSettingsPage as the index
// route under /g/:gameShortId/feedback-settings.
//
// Owns its own list (rather than reading from shell context) so it can
// pull archived forms behind a toggle - same shape as the Builds page.
// Realtime is still driven by the shell-owned channel; we re-pull when
// feedbackFormsVersion bumps.
// ---------------------------------------------------------------------------

export function FormsSection(): JSX.Element {
  const { activeGame, feedbackFormsVersion, refreshFeedbackForms } = useShell();
  const navigate = useNavigate();
  const params = useParams<{ studioShortId: string; gameShortId: string }>();
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [rows, setRows] = useState<FeedbackFormWithCount[]>([]);
  const [showArchived, setShowArchived] = useState(false);

  const reload = useCallback(async (): Promise<void> => {
    if (activeGame === null) {
      setRows([]);
      return;
    }
    const next = await fetchFeedbackFormsForGame(activeGame.id, {
      includeArchived: true,
    });
    setRows(next);
  }, [activeGame]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Realtime updates land via the shell channel. Re-pull when the bump
  // counter changes so archive / unarchive / submission inserts reflow
  // the list without a manual refresh.
  useEffect(() => {
    if (feedbackFormsVersion === 0) return;
    void reload();
  }, [feedbackFormsVersion, reload]);

  const activeRows = useMemo(
    () => rows.filter((r) => r.form.archivedAt === null),
    [rows],
  );
  const archivedCount = useMemo(
    () => rows.length - activeRows.length,
    [rows, activeRows],
  );
  const visibleRows = showArchived ? rows : activeRows;

  if (activeGame === null) {
    // Should not be reachable: the route lives under /g/:gameShortId. Guard
    // anyway so a render race during navigation doesn't blow up.
    return (
      <div className="text-sm text-text-muted">No game selected.</div>
    );
  }

  async function handleCreate(): Promise<void> {
    if (activeGame === null) return;
    setCreating(true);
    setCreateError(null);
    const result = await createFeedbackForm(activeGame.id, 'Untitled form');
    setCreating(false);
    if (!result.ok || result.data === null) {
      setCreateError(
        result.error === 'not_a_member'
          ? "You don't have permission to create forms in this studio."
          : 'Could not create the form. Please try again.',
      );
      return;
    }
    // Keep shell-side list (used by other surfaces) in sync, then jump
    // into the builder for the new form.
    await refreshFeedbackForms();
    if (params.studioShortId === undefined || params.gameShortId === undefined) return;
    navigate(
      `/s/${params.studioShortId}/g/${params.gameShortId}/feedback-settings/forms/${result.data.id}`,
    );
  }

  const isEmpty = activeRows.length === 0 && archivedCount === 0;

  // Starter banner: shows when the seeded form is the only active form
  // and the studio hasn't dismissed it. Disappears as soon as a second
  // form lands (manual create) or the studio renames the starter - both
  // signal "I know what this is, stop reminding me". Archived forms
  // don't count, so the banner stays dormant after the studio archives
  // everything except the seed.
  const starterCandidate =
    activeRows.length === 1 && activeRows[0]?.form.name === STARTER_FORM_NAME
      ? activeRows[0].form
      : null;
  const starterId = starterCandidate?.id ?? null;
  const [starterDismissed, setStarterDismissed] = useState<boolean>(false);
  useEffect(() => {
    if (starterId === null) {
      setStarterDismissed(false);
      return;
    }
    setStarterDismissed(
      window.localStorage.getItem(`${BANNER_STORAGE_PREFIX}${starterId}`)
      === 'true',
    );
  }, [starterId]);

  function handleDismissStarter(): void {
    if (starterId === null) return;
    window.localStorage.setItem(`${BANNER_STORAGE_PREFIX}${starterId}`, 'true');
    setStarterDismissed(true);
  }

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-6 py-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-2xl font-semibold text-text">Feedback forms</h1>
        <p className="text-sm text-text-muted">
          Build short forms players fill out from Discord. Submissions land in your
          Feedback inbox alongside everything else.
        </p>
      </header>

      {createError !== null ? (
        <Alert tone="danger">{createError}</Alert>
      ) : null}

      {starterCandidate !== null && !starterDismissed ? (
        <Alert tone="info">
          <div className="flex items-start gap-3">
            <span className="flex-1">
              We added a starter form to get you going - have a look, edit
              it, and enable it when you&rsquo;re ready.
            </span>
            <button
              type="button"
              onClick={handleDismissStarter}
              className="shrink-0 text-xs font-semibold uppercase tracking-wide text-info-text hover:underline"
            >
              Dismiss
            </button>
          </div>
        </Alert>
      ) : null}

      {isEmpty ? (
        <EmptyState
          title="No feedback forms yet"
          description="Create a form to ask players a few targeted questions when they finish a session."
          cta={
            <Button variant="primary" onClick={() => void handleCreate()} disabled={creating}>
              {creating ? 'Creating...' : 'Create your first form'}
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            {archivedCount > 0 ? (
              <div className="flex items-center gap-2 text-xs text-text-muted">
                <span id="show-archived-forms-label">
                  Show archived ({archivedCount})
                </span>
                <Toggle
                  aria-label="Show archived forms"
                  aria-labelledby="show-archived-forms-label"
                  checked={showArchived}
                  onCheckedChange={setShowArchived}
                />
              </div>
            ) : (
              <span />
            )}
            <Button variant="primary" onClick={() => void handleCreate()} disabled={creating}>
              {creating ? 'Creating...' : 'Create form'}
            </Button>
          </div>
          <FormsList rows={visibleRows} />
        </div>
      )}
      </div>
    </div>
  );
}
